import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { supabase } from "@/integrations/supabase/client";
import { getMyOnboardingStatus } from "@/lib/applications.functions";
import { getMyCandidateProfile } from "@/lib/cv-parse.functions";

export const Route = createFileRoute("/_authenticated/bartender/rejected")({
  head: () => ({ meta: [{ title: "Application Unsuccessful — The Hospitality Reserve" }] }),
  component: RejectedPage,
});

function RejectedPage() {
  const navigate = useNavigate();
  const status = useServerFn(getMyOnboardingStatus);
  const profileFn = useServerFn(getMyCandidateProfile);
  const [months, setMonths] = useState<number | null>(null);

  useEffect(() => {
    (async () => {
      const s = await status();
      if (s.status !== "rejected") { navigate({ to: "/dashboard", replace: true }); return; }
      const p = await profileFn();
      if (!("error" in p) && p.has_6_months_exp === false) {
        setMonths(p.total_months_experience ?? 0);
      }
    })();
  }, []);

  const signOut = async () => {
    await supabase.auth.signOut();
    navigate({ to: "/auth", replace: true });
  };

  return (
    <div className="min-h-screen bg-background text-foreground flex flex-col">
      <header className="px-6 py-5 flex items-center justify-between border-b border-border">
        <div>
          <p className="tracking-wide-luxury text-accent text-xs">The Hospitality Reserve</p>
          <p className="font-display text-lg leading-tight">Hospitality</p>
        </div>
        <button onClick={signOut} className="text-xs tracking-wide-luxury text-muted-foreground hover:text-accent">
          Sign out
        </button>
      </header>
      <main className="flex-1 flex items-center justify-center px-5 py-12">
        <div className="w-full max-w-md text-center">
          <p className="tracking-wide-luxury text-accent text-xs">Application status</p>
          <span className="block mx-auto mt-5 h-px w-12 bg-accent/60" />
          <h1 className="mt-6 font-display text-3xl">Unsuccessful application</h1>
          {months != null ? (
            <p className="mt-4 text-sm text-muted-foreground leading-relaxed">
              Unfortunately, our platform requires a minimum of 6 months combined bar experience to accept
              shifts. We found {months} month{months === 1 ? "" : "s"} of relevant experience on your CV, so
              your profile could not be approved at this time.
            </p>
          ) : (
          <p className="mt-4 text-sm text-muted-foreground leading-relaxed">
            Thank you for applying to The Hospitality Reserve. Unfortunately, after reviewing your application
            details, you have been unsuccessful on this occasion. We appreciate your time and interest, and wish
            you the best of luck with your future endeavors.
          </p>
          )}
        </div>
      </main>
    </div>
  );
}
